import React, { useState, useEffect, useRef, useContext } from 'react';

import 'react-datepicker/dist/react-datepicker.css';
import { format } from 'date-fns';
import { useNavigate, useLocation } from 'react-router-dom';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from 'react-icons/md';
import { IoMdCheckmark } from 'react-icons/io';
import { Layout, Header} from '../../../../components';
import { handleApi } from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ApproveQuantityAdjustment = () => {
  const location = useLocation();
  const { quantity_adjustment } = location.state || {};
  const { userData, currentColor, actionCount, setActionCount } = useContext(AppContext);
  const emptyTransactionLine = {
    product_id: null,
    product_name: '',
    unit_of_measure: '',
    ordered_quantity: 0,
    adjusted_quantity: '',
  };
  
  const [quantityAdjustments, setQuantityAdjustments] = useState(() => {
    if (quantity_adjustment) {
      const lines = (quantity_adjustment.transactionLines || []).map(line => ({
        product_id: line.product_id || null,
        product_name: line.products.name || '',
        unit_of_measure: line.prices.sell_uom_name|| '',
        ordered_quantity: line.old_quantity || 0,
        adjusted_quantity: line.new_quantity || '',
      }));
  
      return {
        id: quantity_adjustment.id,
        adjustment_date: quantity_adjustment?.adjustment_date ?? new Date().toISOString(),
        description: quantity_adjustment.description || '',
        approved: quantity_adjustment.approved || 0,
        transactionLines: lines.length > 0 ? lines : [emptyTransactionLine],
      };
    }
  
  });
  
  const [selectedAdjustmentId, setSelectedAdjustmentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openAdjustmentId, setOpenAdjustmentId] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const navigate = useNavigate();
  const handleApprove = () => { setIsConfirmModalOpen(true); };// show confirmation modal
  
  const confirmApprove = async () => {
    setIsConfirmModalOpen(false); // Close confirmation modal
    setIsLoading(true); // Show loading spinner
    const apiEndpoint = `quantity_adjustments`; // Replace with your actual approval endpoint
    const apiMethod = 'PUT'; // or POST depending on your backend
    const data = {id:quantityAdjustments.id,approved: 1,};
    const parameters = { };
    try {
      const response = await handleApi(apiEndpoint, data, userData, apiMethod, parameters);
      if (response.status === 200) {
        toast.success('Quantity Adjustment Approved!');
        setQuantityAdjustments(prev => ({ ...prev,approved: 1,}));// Update state to reflect approval
        setActionCount(prev => prev + 1); //  increment action count or trigger refresh
      } else {toast.error(`Approval failed: ${response.statusText}`);}
    } catch (error) {
      toast.error('Error approving adjustment: ' + error.message);
    } finally {
      setIsLoading(false);
      setActionCount(actionCount + 1);
    }
  };
    
  const handleClose = () => { navigate(-1);   }; // Navigate back when closing the form
  return (
    <Layout>
      {/* <div className="m-4 bg-gray-900 rounded-2xl shadow-xl p-6"> */}
      <div className=" bg-gray-800 rounded-xl shadow-2xl p-2">
        <ToastContainer position="top-center" autoClose={2000} />
        <div className="text-center py-4 border-b border-gray-700">
          <div className="w-full flex justify-end">
            <button
              className="text-white hover:text-gray-400 py-1 px-2 rounded-full transition-all duration-300"
              title="Close"
              onClick={handleClose}
            >
              <MdOutlineCancel size={24} color={currentColor} />
            </button>
          </div>
          <div className="mx-4 text-white">
            <Header category="Inventory" title="Approve Quantity Adjustment" color={currentColor} />
          </div>
        </div>

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center py-6">
            <Audio height="80" width="80" color="white" ariaLabel="loading" />
            <p className="mt-4 text-white text-sm">Processing quantity_adjustment...</p>
          </div>
        ) : (
          // <div className="bg-gray-800 p-6 my-4 rounded-2xl shadow-xl max-w-full mx-auto">
          <div className="bg-white px-24 py-8  my-4 rounded-xl shadow-2xl w-full">
            <div className="space-y-6">
              <div
                key={quantityAdjustments.id}
                className="bg-gradient-to-br from-gray-700 to-gray-800 text-white rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-all duration-300 w-full"
              >
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  {/* Adjustment ID & Status */}
                  <div className="flex-1 text-center md:text-left">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                      <p className="text-sm font-semibold text-gray-300 tracking-wide">
                        Adjustment <span className="text-blue-400 font-bold">#{quantityAdjustments.id}</span>
                      </p>
                      <span
                        className={`px-3 py-1 text-xs font-semibold rounded-full shadow-md transition 
                          ${
                            quantityAdjustments.approved === 0
                              ? 'bg-yellow-500 text-black'
                              : quantityAdjustments.approved === 1
                              ? 'bg-green-500 text-white'
                              : 'bg-gray-500 text-white'
                          }`}
                      >
                        {quantityAdjustments.approved === 0
                          ? 'In Draft'
                          : quantityAdjustments.approved === 1
                          ? 'Approved'
                          : 'Unknown'}
                      </span>
                    </div>
                  </div>

                  {/* Date Picker Toggle */}
                  <div className="relative flex-1 text-center">
                    <button
                      onClick={() =>
                        setOpenAdjustmentId(quantityAdjustments.id) }
                      className="text-xs text-gray-400 hover:text-blue-400 transition flex flex-col items-center"
                    >
                      {quantityAdjustments.adjustment_date && !isNaN(new Date(quantityAdjustments.adjustment_date).getTime()) ? (
                        <>
                          <span className="font-medium">
                            {format(new Date(quantityAdjustments.adjustment_date), 'MMM d')}
                          </span>
                          <span className="text-xs text-gray-500">
                            {format(new Date(quantityAdjustments.adjustment_date), 'yyyy')}
                          </span>
                        </>
                      ) : ( <span className="text-red-500 text-xs">Invalid date</span>
                      )}
                     </button>
                  </div>

                  {/* Toggle Button */}
                  <div
                    onClick={() =>setSelectedAdjustmentId(quantityAdjustments.id)}
                    className={`w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center cursor-pointer transition-transform duration-300 ${
                      selectedAdjustmentId === quantityAdjustments.id ? 'rotate-45' : ''
                    }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </div>
                </div>

                {/* Reason for Adjustment */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Reason for Adjustment
                  </label>
          <em
            className="italic text-gray-400 hover:text-blue-400 cursor-pointer transition"
          >
           {String(quantityAdjustments.description || '').trim() || 'N/A'}
            {/* {quantityAdjustments.description?.trim() || 'Click to select a reason...'} */}
          </em>
      </div>
    </div>
  </div>

{/* Delivered Transaction Lines (Display Items and Allow Data Input) */}
      <div className="mt-3 p-4 bg-gray-700 rounded-2xl w-full transition-all duration-300">
    {/* Table Header */}
    <div className="grid grid-cols-[minmax(100px,1fr)_80px_120px_90px] gap-4 text-sm p-4 border-b text-gray-300 font-semibold">
      <div className="text-left">Product</div>
      <div className="text-center">Uom</div>
      <div className="text-center">Original Qty</div>
      <div className="text-center">Adjusted Qty</div>
    </div>

    {/* Table Content */}
    {quantityAdjustments.transactionLines.length > 0 ? (
      quantityAdjustments.transactionLines.map((line, index) => (
        <div
          key={index}
          className="grid grid-cols-[minmax(100px,1fr)_80px_120px_90px] gap-4 px-4 border-b bg-gray-800 hover:bg-gray-700 text-sm transition-all duration-200"
        >
            <div className="mt-2 text-gray-300">{line.product_name}</div>
            <div className="text-center mt-2 text-gray-300">{line.unit_of_measure}</div>
            <div className="text-center text-gray-300 mt-2">{Math.round(line.ordered_quantity)}</div>
            <div className="text-center mt-2 text-gray-300">{line.adjusted_quantity}</div>
        </div>
      ))
    ) : (
      <div className="text-center text-gray-500 py-4">No order details found</div>
    )}
  </div>

  {/* Action Buttons */}
  <div className="flex justify-center space-x-6 mt-6">
    <button
      onClick={handleApprove}
      className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-all duration-300 flex items-center gap-2"
    >
      <IoMdCheckmark size={20} />
      <span>Approve</span>
        </button>

              <button
                onClick={handleClose}
                className="bg-blue-500 text-white px-6 py-3 rounded-xl hover:bg-blue-600 transition-all duration-300 flex items-center gap-2"
              >
                <MdOutlineCancel size={20} />
                <span>Close</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {isConfirmModalOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 backdrop-blur-sm transition-all">
    <div className="bg-gray-900 rounded-xl shadow-2xl p-8 w-full max-w-md text-center border border-gray-700">
      <h2 className="text-2xl font-bold text-white mb-4">Confirm Approval</h2>
      <p className="text-gray-400 mb-6">
        Are you sure you want to <span className="text-green-400 font-semibold">approve</span> this quantity adjustment?
      </p>
      <div className="flex justify-center gap-6">
        <button
          onClick={confirmApprove}
          className="px-5 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
        >
          Yes, Approve
        </button>
        <button
          onClick={() => setIsConfirmModalOpen(false)}
          className="px-5 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
    </Layout>
  );
};
export default ApproveQuantityAdjustment;
